import { stagger } from '@/lib/motion';
import { Icon } from '@/components/Icon';
import { Kicker } from '@/components/Kicker';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { percentOf, statusOf, type ProgressMap } from './progressModel';
import { lessonMeta, type LessonGroup, type TutorialLesson } from './tutorialsModel';

/** The card's picture: the first step's screenshot, or the lesson icon on a quiet tile, with a play button. Fixed 16:9 so nothing reflows. */
function Thumbnail({ lesson }: { lesson: TutorialLesson }) {
  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-t-lg border-b bg-secondary">
      {lesson.thumbnail ? (
        <img src={lesson.thumbnail} alt="" loading="lazy" className="size-full object-cover object-top" />
      ) : (
        <div className="flex size-full items-center justify-center text-subtle">
          <Icon name={lesson.icon} size={36} />
        </div>
      )}
      <span aria-hidden className="absolute bottom-2 left-2 flex size-8 items-center justify-center rounded-md border bg-card text-foreground shadow-card">
        <Icon name={lesson.icon} size={18} />
      </span>
      <span aria-hidden data-testid={`lesson-card-play-${lesson.id}`} className="absolute inset-0 flex items-center justify-center">
        <span className="flex size-12 items-center justify-center rounded-full bg-foreground/75 text-background shadow-card transition-transform duration-150 ease-out group-hover:scale-110 group-focus-visible:scale-110 motion-reduce:transition-none">
          <Icon name="play_arrow" size={28} fill />
        </span>
      </span>
    </div>
  );
}

function ProgressLine({ lesson, progress }: { lesson: TutorialLesson; progress: ProgressMap }) {
  const status = statusOf(progress, lesson);
  if (status === 'done') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-succeeded">
        <Icon name="check_circle" size={16} fill />
        Completed
      </span>
    );
  }
  if (status === 'new') return <span className="text-xs text-subtle">Not started</span>;
  const percent = percentOf(progress, lesson);
  return (
    <span className="flex w-full items-center gap-2">
      <Progress value={percent} aria-label={`${percent}% watched`} className="h-1 flex-1" indicatorClassName="bg-foreground/60" />
      <span className="tabular text-xs text-muted-foreground">{percent}%</span>
    </span>
  );
}

function LessonCard({ lesson, progress, index, onOpen }: { lesson: TutorialLesson; progress: ProgressMap; index: number; onOpen: (id: string) => void }) {
  return (
    <li className="enter min-w-0" style={stagger(index)}>
      <button
        type="button"
        data-testid={`lesson-card-${lesson.id}`}
        data-status={statusOf(progress, lesson)}
        onClick={() => onOpen(lesson.id)}
        className="lift group flex h-full w-full flex-col rounded-lg border bg-card text-left shadow-card outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <Thumbnail lesson={lesson} />
        <span className="flex flex-1 flex-col gap-1.5 p-4">
          <span className="text-[15px] font-semibold leading-snug text-foreground">{lesson.title}</span>
          {lesson.summary ? <span className="line-clamp-2 text-[13px] text-muted-foreground">{lesson.summary}</span> : null}
          <span className="mt-auto flex flex-col gap-2 pt-2">
            <span className="tabular text-xs text-subtle">{lessonMeta(lesson)}</span>
            <ProgressLine lesson={lesson} progress={progress} />
          </span>
        </span>
      </button>
    </li>
  );
}

function GroupHeading({ group }: { group: LessonGroup }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
      <Kicker>
        {group.operator ? <Icon name="shield" size={16} className="text-muted-foreground" /> : null}
        {group.title}
      </Kicker>
      <span className="tabular text-xs text-subtle">
        {group.lessons.length} {group.lessons.length === 1 ? 'lesson' : 'lessons'}
      </span>
      {group.operator ? (
        <Badge variant="outline" data-testid="operator-group-badge">
          Platform operators only
        </Badge>
      ) : null}
    </div>
  );
}

/** Lessons grouped as the model orders them (operator lessons first, in their own labeled group). */
export function LessonCatalog({ groups, progress, onOpen }: { groups: LessonGroup[]; progress: ProgressMap; onOpen: (id: string) => void }) {
  let order = 0;
  return (
    <div className="flex flex-col gap-8">
      {groups.map((group) => (
        <section key={group.id} data-testid={`lesson-group-${group.id}`} data-operator={group.operator || undefined} aria-label={group.title} className="flex flex-col gap-3">
          <GroupHeading group={group} />
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {group.lessons.map((lesson) => (
              <LessonCard key={lesson.id} lesson={lesson} progress={progress} index={order++} onOpen={onOpen} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/** The catalog's real geometry while the manifest loads: a group label and three 16:9 cards. */
export function LessonCatalogSkeleton() {
  return (
    <div data-testid="tutorials-skeleton" aria-busy className="flex flex-col gap-3">
      <Skeleton className="h-4 w-40" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex flex-col overflow-hidden rounded-lg border bg-card shadow-card">
            <Skeleton className="aspect-video w-full rounded-none" />
            <div className="flex flex-col gap-2 p-4">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
